import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Modal,
  notification,
  Form,
  Input,
} from "lib/ui";
import { DeleteOutlined } from "lib/icons";
import {
  ArrowLeftRight,
  Wallet,
  CheckCircle,
  AlertCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import fetchApi from "../../../hooks/ApiFetch";
import { AuthContext } from "../../../context/Context";
import { convertToReadableDate } from "../../Data/Data";
import {
  AdminPageLayout,
  AdminHero,
  AdminStats,
  AdminSearchBar,
  AdminFilterTabs,
} from "../../../components/admin/AdminPageShell";
import {
  PeopleDataTable,
  ActionBtn,
  BoolPill,
} from "../../../components/admin/PeopleDataTable";

const PAY_FILTERS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "unpaid", label: "Unpaid" },
];

function formatAED(n) {
  return Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const TransactionsPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [searchId, setSearchId] = useState("");
  const [searchName, setSearchName] = useState("");
  const [payFilter, setPayFilter] = useState("all");

  const { Users, order: data, fetchOrder, setorders: setData } = useContext(AuthContext);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const getName = (id) => {
    const user = Users?.find((u) => u._id === id);
    return user?.name || user?.firstname || "Unknown";
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleDeleteUser = (id) => {
    Modal.confirm({
      title: "Delete this transaction?",
      icon: <DeleteOutlined />,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await fetchApi.delete(`/orders/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Removed",
            description: "Transaction record deleted.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete record.",
          });
        }
      },
    });
  };

  const filteredData = useMemo(() => {
    const qId = searchId.trim();
    const qName = searchName.trim().toLowerCase();
    return [...(data || [])]
      .filter((item) => item?.position !== "driver")
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .filter((item) => {
        if (qId && !String(item.ref ?? "").includes(qId)) return false;
        if (qName && !getName(item.userId).toLowerCase().includes(qName))
          return false;
        const unpaid = Number(item.balance) > 0;
        if (payFilter === "paid" && unpaid) return false;
        if (payFilter === "unpaid" && !unpaid) return false;
        return true;
      });
  }, [data, searchId, searchName, payFilter, Users]);

  const stats = useMemo(() => {
    const list = filteredData;
    const revenue = list.reduce((s, o) => s + Number(o.totalPrice ?? 0), 0);
    const unpaid = list.filter((o) => Number(o.balance) > 0).length;
    const paid = list.length - unpaid;
    return { count: list.length, revenue, unpaid, paid };
  }, [filteredData]);

  const columns = [
    {
      key: "ref",
      header: "Order ref",
      render: (row) => (
        <span className="font-mono text-sm font-bold text-brand-dark">
          #{row.ref ?? "—"}
        </span>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      render: (row) => (
        <span className="font-medium text-slate-900">{getName(row.userId)}</span>
      ),
    },
    {
      key: "pickup",
      header: "Pick-up",
      render: (row) => (
        <span className="text-xs text-slate-600">
          {convertToReadableDate(row.pickTime)}
        </span>
      ),
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      render: (row) => (
        <span className="tabular-nums font-semibold">
          {formatAED(row.totalPrice)} AED
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (row) => (
        <span
          className={`tabular-nums font-bold ${
            Number(row.balance) > 0 ? "text-amber-700" : "text-emerald-700"
          }`}
        >
          {formatAED(row.balance)}
        </span>
      ),
    },
    {
      key: "payment",
      header: "Payment",
      render: (row) => (
        <BoolPill
          value={Number(row.balance) <= 0}
          trueLabel="Paid"
          falseLabel="Unpaid"
        />
      ),
    },
    {
      key: "mode",
      header: "Method",
      render: (row) => (
        <span className="text-xs font-medium capitalize text-slate-600">
          {row.paymentmode || "—"}
        </span>
      ),
    },
    {
      key: "created",
      header: "Date",
      render: (row) => (
        <span className="text-xs text-slate-500">
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </span>
      ),
    },
  ];

  return (
    <AdminPageLayout>
      <AdminHero
        icon={ArrowLeftRight}
        badge="Finance"
        title="Transactions"
        description="Order payment ledger — track paid and outstanding balances by customer and reference."
      />

      <AdminStats
        items={[
          { label: "Showing", value: stats.count, icon: ArrowLeftRight },
          {
            label: "Volume",
            value: `${formatAED(stats.revenue)} AED`,
            icon: Wallet,
          },
          { label: "Paid", value: stats.paid, icon: CheckCircle },
          { label: "Unpaid", value: stats.unpaid, icon: AlertCircle },
        ]}
      />

      <AdminSearchBar
        placeholder="Search order reference…"
        value={searchId}
        onChange={setSearchId}
        extra={
          <>
            <AdminFilterTabs
              tabs={PAY_FILTERS}
              active={payFilter}
              onChange={setPayFilter}
            />
            <Input
              placeholder="Search customer name…"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-full"
            />
          </>
        }
      />

      <PeopleDataTable
        wide
        columns={columns}
        rows={filteredData}
        rowKey="_id"
        renderActions={(row) => (
          <>
            <ActionBtn variant="edit" onClick={() => handleEditUser(row)}>
              <Pencil size={14} />
              Edit
            </ActionBtn>
            <ActionBtn
              variant="delete"
              onClick={() => handleDeleteUser(row._id)}
            >
              <Trash2 size={14} />
              Delete
            </ActionBtn>
          </>
        )}
        emptyTitle="No transactions found"
        emptyHint="Adjust filters or search terms."
      />

      <Modal
        title={editingUser ? "Edit order" : "Add order"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      />
    </AdminPageLayout>
  );
};

export default TransactionsPage;
